<template>
  <TemplateBase>
    <main class="relative z-0 col-span-full row-start-2">
      <!-- Breadcrumbs -->
      <OrganismNavigationBreadcrumb class="mx-auto p-4 md:container md:mb-16 md:pb-0 md:pt-10 lg:mb-20" :path="breadcrumbs" />

      <section id="department-overview-content" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <div class="rounded-xl md:bg-white">
          <div class="page-padding md:pt-12 lg:pt-20">
            <div v-if="page.departmentType.introduction" class="flex items-center mb-3 p-3 bg-app-green-200 rounded-xl md:mb-10 md:p-6">
              <div class="inline-flex justify-center mr-3 p-3 bg-white rounded-xl md:mr-8 md:p-4">
                <AtomIcon :icon="get(page, 'departmentType.icon.slug')" class="w-10 h-10 md:w-16 md:h-16 text-app-green" />
              </div>
              <h2 class="prose prose--app text-sm uppercase md:text-3xl" v-html="get(page, 'departmentType.introduction')" />
            </div>

            <h1 v-if="page.title" class="mb-4 text-black text-lg font-bold leading-5 uppercase md:mb-10 md:text-3xl">
              {{ page.title }}
            </h1>

            <OrganismLocationSearchInput
              id="department-overview-search"
              class="mb-4 max-w-sm"
              :location="location"
              :distance="distance"
              :city="city"
              :errors="errors"
              @location="setLocation"
              @distance="setDistance"
            />
          </div>

          <hr class="mt-6 border-t-2 border-solid border-app-green md:border-t-4 md:border-app-green-100 md:border-white" />

          <div
            v-if="locationType"
            class="page-padding flex items-center -mb-4 mt-8 py-4 text-app-green font-bold border-b border-solid border-app-green-100 md:mb-0 md:py-8 md:text-lg"
          >
            <span v-show="locationType === 'city'">{{ $t('department-overview-city-results', { search: city }) }}</span>
            <span v-show="locationType === 'nl_sixpp'">{{ $t('department-overview-postal-code-results', { search: city }) }}</span>
          </div>

          <ul>
            <li
              v-for="(departmentDetails, index) in departments"
              :key="index"
              class="page-padding md:hover:bg-white hover:bg-app-green-200 border-b-2 border-solid border-white transform transition duration-200 md:border-b md:border-app-green-100"
              :class="{ 'py-4': index < departments.length - 1, 'pt-4  pb-12': index === departments.length - 1 }"
            >
              <AtomLink :url="getUrl(departmentDetails)" target="_self">
                <h5 class="text-black text-lg font-bold">
                  {{ get(departmentDetails, 'department.title') }}
                </h5>
                <p class="text-black text-lg">
                  {{ get(departmentDetails, 'department.address.city') }}
                </p>
              </AtomLink>
            </li>
          </ul>
        </div>
      </section>

      <OrganismPagination id="department-overview-paging" :count="totalHits" :limit="hitsPerPage" :page="pageNumber" class="mb-16" @click="({ page }) => setPage(page)" />
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { computed, watch } from 'vue'
import get from 'lodash/get'
import { routerQueryIsEqual } from '~/composables/setup-dynamic-url'
import { setupSiteConfig } from '@/composables/craft'
import { useDepartmentOverviewStore } from '@/store/department-overview'

interface Props {
  breadcrumbs: Array<any>
  page: object
  limit?: number
}

defineProps<Props>()

const { domain } = setupSiteConfig()
const getUrl = ({ landing, shelterDetail, department }) => {
  if (department.urlOverride) {
    return department.urlOverride
  }

  switch (domain) {
    case 'ikZoekBaas':
    case 'mijnDierIsZoek':
      return shelterDetail.url

    default:
      return landing.url
  }
}

const route = useRoute()
const router = useRouter()
const departmentOverviewStore = useDepartmentOverviewStore()

const city = computed(() => departmentOverviewStore.city)
const departments = computed(() => departmentOverviewStore.departments)
const distance = computed(() => departmentOverviewStore.distance)
const errors = computed(() => departmentOverviewStore.errors)
const hitsPerPage = computed(() => departmentOverviewStore.hitsPerPage)
const location = computed(() => departmentOverviewStore.location)
const locationType = computed(() => departmentOverviewStore.type)
const pageNumber = computed(() => departmentOverviewStore.page)
const totalHits = computed(() => departmentOverviewStore.totalHits)
const urlParameters = computed(() => departmentOverviewStore.urlParameters)

// Watcher for router query comparison
watch(urlParameters, (query) => {
  if (!routerQueryIsEqual(route.query, query)) {
    router.push({ query })
  }
})

const setDistance = departmentOverviewStore.setDistance
const setLocation = departmentOverviewStore.setLocation
const setPage = departmentOverviewStore.setPage
</script>

<style lang="scss" scoped>
.page-padding {
  @apply px-4;

  @screen md {
    @apply px-12;
  }

  @screen lg {
    @apply px-20;
  }

  @screen 2xl {
    @apply px-24;
  }
}
</style>
